import axios, { RawAxiosRequestHeaders } from 'axios';

import { PROFILE_LOGIN_DATA_PATH } from '../constants/profile';
import { ApiRequestContext, getApiRequestContext } from './apiContext';

class Api {
  private readonly apiUri: string;

  private readonly instance: ApiRequestContext = {
    location: null,
    prefix: null,
  };

  constructor() {
    this.apiUri = 'api/graphql';
  }

  private context(): ApiRequestContext {
    return getApiRequestContext() ?? this.instance;
  }

  setLocation(value: Location) {
    if (value) {
      this.context().location = value;
    }
  }

  getLocation() {
    if (__CLIENT__) {
      return window.location;
    }
    return this.context().location;
  }

  getPrefix() {
    return this.context().prefix;
  }

  setPrefix(value: string) {
    this.context().prefix = value;
  }

  setProfileHost(value?: string) {
    const host = value?.trim();

    this.context().profileHost = host
      ? /^https?:\/\//i.test(host)
        ? host
        : `https://${host}`
      : null;
  }

  getProfileUrl(path: string) {
    const profileHost = this.context().profileHost;

    if (!profileHost) {
      throw new Error('profileHost is not configured');
    }

    return new URL(path, profileHost).toString();
  }

  setUserAgent(value: string) {
    this.context().userAgent = value;
  }

  setCookie(value: string) {
    this.context().cookie = value;
  }

  getApiHost() {
    return __SERVER__ ? __SSR_URL__ : this.getLocation()?.origin;
  }

  async callGraphQL<T>(
    query: string,
    variables: Record<string, any> = {},
    files: Record<string, File | File[]> = null
  ): Promise<T> {
    let res;
    let data:
      | FormData
      | {
          query: string;
          variables: Record<string, any>;
        };

    if (files && Object.values(files).filter(Boolean).length) {
      const formData = new FormData();
      const map: Record<string, string[]> = {};

      let index = 0;
      Object.entries(files).forEach(([key, value]: [string, File | File[]]) => {
        if (files[key]) {
          if (Array.isArray(value)) {
            variables[key] = value.map((): null => null);
            value.forEach(() => {
              map[`${index}`] = [`variables.${key}.${index}`];
              index++;
            });
          } else {
            variables[key] = null;
            map[`${index}`] = [`variables.${key}`];
            index++;
          }
        }
      });

      formData.append(
        'operations',
        JSON.stringify({
          query,
          variables,
        })
      );
      formData.append('map', JSON.stringify(map));

      index = 0;
      Object.values(files).forEach((value) => {
        if (value) {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              formData.append(`${index++}`, v);
            });
          } else {
            formData.append(`${index++}`, value);
          }
        }
      });
      data = formData;
    } else {
      data = {
        query,
        variables,
      };
    }

    try {
      const url = `${this.getApiHost()}/${this.apiUri}/`;

      res = await axios({
        url,
        method: 'POST',
        withCredentials: true,
        headers: this.getHeaders(data),
        data,
      });
    } catch (e) {
      console.error('RegErr:', e);

      if (__CLIENT__ && window && e.response && e.response?.status === 502) {
        setTimeout(() => {
          window?.location.reload();
        }, 2000);
      }
      throw new Error(e.code || e.response ? e.response?.status : e.message);
    }

    if (res?.status === 200) {
      const body = res.data;
      if (body.errors && body.errors.length > 0) {
        throw body.errors[0];
      }
      return body.data;
    }
    throw new Error(`${res?.status || 'UnknownError'}`);
  }

  async loadI18nMessages(url: string) {
    try {
      const { data } = await axios.get(url, {
        headers: {
          ...this.getHeaders(),
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (data?.messages && typeof data.messages === 'object') {
        return data.messages;
      }

      if (data && typeof data === 'object' && !Array.isArray(data)) {
        return data;
      }
    } catch (e) {
      console.error(e);
    }

    return {};
  }

  async loadLocalI18n(lang: string) {
    if (!__SERVER__) {
      return {};
    }

    try {
      const fs = await import('node:fs/promises');
      const path = await import('node:path');
      const filePath = path.resolve(process.cwd(), 'i18n', `${lang}.json`);
      const raw = await fs.readFile(filePath, 'utf-8');
      const messages = JSON.parse(raw.replace(/^\uFEFF/, ''));

      if (messages && typeof messages === 'object') {
        return messages;
      }
    } catch (e) {
      console.error(e);
    }

    return {};
  }

  async loadLoginData<T>(): Promise<T> {
    try {
      const url = __SERVER__
        ? `${this.getApiHost()}${PROFILE_LOGIN_DATA_PATH}`
        : this.getProfileUrl(PROFILE_LOGIN_DATA_PATH);

      const res = await axios({
        url,
        method: 'GET',
        withCredentials: true,
        headers: this.getHeaders(),
      });

      if (res?.status === 200) {
        return res.data;
      }

      throw new Error(`${res?.status || 'UnknownError'}`);
    } catch (e) {
      console.error('loadLoginData:', e);
      throw new Error(e.code || e.response ? e.response?.status : e.message);
    }
  }

  async loadI18n(lang: string, staticVersion?: number) {
    const v = staticVersion || Date.now();
    const apiHost = this.getApiHost();
    const localMessages = __SERVER__ ? await this.loadLocalI18n(lang) : {};
    const defaultMessages =
      __SERVER__ && lang !== 'en' ? await this.loadLocalI18n('en') : {};

    if (
      __SERVER__ &&
      Object.keys({ ...defaultMessages, ...localMessages }).length
    ) {
      return {
        ...defaultMessages,
        ...localMessages,
      };
    }

    try {
      let messages = apiHost
        ? await this.loadI18nMessages(
            `${apiHost}/api/i18n/ai/v${v}/${lang}.json`
          )
        : {};

      if (!Object.keys(messages).length && apiHost) {
        messages = await this.loadI18nMessages(
          `${apiHost}/api/i18n/ai/${lang}.json?v=${v}`
        );
      }

      const origin = this.getLocation()?.origin;
      if (origin) {
        let fileMessages = await this.loadI18nMessages(
          `${origin}/dev/i18n/v${v}/${lang}.json`
        );

        if (!Object.keys(fileMessages).length) {
          const [enMessages, langMessages] = await Promise.all([
            lang === 'en'
              ? Promise.resolve({})
              : this.loadI18nMessages(`${origin}/sync/i18n/en.json`),
            this.loadI18nMessages(`${origin}/sync/i18n/${lang}.json`),
          ]);

          fileMessages = {
            ...enMessages,
            ...langMessages,
          };
        }

        messages = {
          ...messages,
          ...fileMessages,
        };
      }

      return {
        ...defaultMessages,
        ...localMessages,
        ...messages,
      };
    } catch (e) {
      console.error(e);
    }

    return {
      ...defaultMessages,
      ...localMessages,
    };
  }

  getHeaders(
    data?:
      | FormData
      | {
          query: string;
          variables: Record<string, any>;
        }
  ): RawAxiosRequestHeaders {
    const headers: RawAxiosRequestHeaders = {
      'Content-Type':
        data && data instanceof FormData
          ? 'multipart/form-data'
          : 'application/json;charset=utf-8',
    };

    if (__SERVER__) {
      const { userAgent, cookie } = this.context();
      const host = this.getLocation()?.host;

      if (host) {
        headers['ssr-host'] = host;
      }
      if (__SSR_KEY__) {
        headers['ssr-key'] = __SSR_KEY__;
      }
      if (userAgent) {
        headers['user-agent'] = userAgent;
      }
      if (cookie) {
        headers.cookie = cookie;
      }
    }

    return headers;
  }
}

export default new Api();
